import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {
    Component
} from "../../../sedestral/framework/interface/component/Component";
import {
    ICommunitySEOClusterKeywordsIncoming
} from "../../../models/community/seo/cluster/ICommunitySEOClusterKeywordsIncoming";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {
    ICommunitySEOClusterKeywordsModel
} from "../../../models/community/seo/cluster/ICommunitySEOClusterKeywordsModel";
import {
    ICommunitySEOClusterKeywordsPreviewModel
} from "../../../models/community/seo/cluster/ICommunitySEOClusterKeywordsPreviewModel";
import {Services} from "../../Services";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {Resources} from "../../../resources/Resources";
import {
    ICommunitySEOClusterKeywordsListIncoming
} from "../../../models/community/seo/cluster/ICommunitySEOClusterKeywordsListIncoming";
import {
    ICommunitySEOClusterKeywordsOnboardingIncoming
} from "../../../models/community/seo/cluster/ICommunitySEOClusterKeywordsOnboardingIncoming";
import {
    ICommunitySEOClusterLocationModel
} from "../../../models/community/seo/cluster/ICommunitySEOClusterLocationModel";

export class CommunityClustersService {

    public static events: ((clusterKeywordsPreviewModel: ICommunitySEOClusterKeywordsPreviewModel[]) => void)[] = [];

    public static dispose(): void {
        this.events = [];
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_SEO_CLUSTER_KEYWORDS_CREATE, (data: ICommunitySEOClusterKeywordsPreviewModel[]) => {
            this.events.forEach(value => value(data));
        });
    }

    public static async create(communityId: string, query: string, parentQueryId: string, component: Component): Promise<ICommunitySEOClusterKeywordsPreviewModel> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.TASK_IN_PROGRESS,
                message: Resources.t("words.topicClusterInProgress")
            },
        ]);

        let request = await Network.post(ProductType.COMMUNITY, `/community/clusters/keywords/create/${communityId}/${query}/${parentQueryId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async createLocal(communityId: string, query: string, locationCode: number, component: Component): Promise<ICommunitySEOClusterKeywordsPreviewModel> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.TASK_IN_PROGRESS,
                message: Resources.t("words.topicClusterInProgress")
            },
            {
                errorCode: ErrorCode.CLUSTER_QUERY_CONTAINS_CITY,
                message: Resources.t("words.topicLocalQueryContainsCity")
            },
        ]);

        let request = await Network.post(ProductType.COMMUNITY, `/community/clusters/keywords/createLocal/${communityId}/${query}/${locationCode}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async searchLocations(communityId: string, query: string, component?: INetworkComponent): Promise<ICommunitySEOClusterLocationModel[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/clusters/keywords/locations/${communityId}/${encodeURIComponent(query)}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return [];
    }

    public static async onboarding(communityId: string, local: boolean, component?: INetworkComponent): Promise<ICommunitySEOClusterKeywordsOnboardingIncoming[]> {
        Services.handleErrors(component, [
            {
                status: HttpStatus.UNAUTHORIZED,
                message: "none"
            },
        ]);

        let request = await Network.post(ProductType.COMMUNITY, `/community/clusters/keywords/onboarding/${communityId}/${local}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return [];
    }

    public static async updateAutopilot(communityId: string, queryId: string, state: boolean, component?: INetworkComponent): Promise<ICommunitySEOClusterKeywordsListIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/clusters/keywords/autopilot/${communityId}/${queryId}/${state}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async createManually(communityId: string, queryId: string, mainKeyword: string, enableAutocompletion: boolean, component?: INetworkComponent, locationCode?: number): Promise<ICommunitySEOClusterKeywordsModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/clusters/keywords/createManually/${communityId}/${queryId}/${mainKeyword}/${enableAutocompletion}/${locationCode ?? 0}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async update(communityId: string, clusterId: string, keywords: ICommunitySEOClusterKeywordsIncoming[], component: Component): Promise<ICommunitySEOClusterKeywordsModel> {
        Services.handleErrors(component, [
            {
                errorCode: ErrorCode.SIZE_MIN,
                message: Resources.t("words.minKeywords")
            },
            {
                errorCode: ErrorCode.SIZE_MAX,
                message: Resources.t("words.maxKeywords")
            },
            {
                errorCode: ErrorCode.MAX_LIMIT,
                message: Resources.t("words.maxClusterModification")
            }
        ]);

        let request = await Network.putJson(ProductType.COMMUNITY, `/community/clusters/keywords/update/${communityId}/${clusterId}`, keywords, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getPreviewsByCommunityId(communityId: string, component: Component): Promise<ICommunitySEOClusterKeywordsPreviewModel[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/clusters/keywords/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getClusters(communityId: string, clusterId: string, component: Component): Promise<ICommunitySEOClusterKeywordsListIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/clusters/keywords/${communityId}/${clusterId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async delete(communityId: string, id: string, component: Component): Promise<ICommunitySEOClusterKeywordsModel> {
        let request = await Network.delete(ProductType.COMMUNITY, `/community/clusters/${communityId}/${id}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async rename(communityId: string, queryId: string, newName: string, component?: INetworkComponent): Promise<ICommunitySEOClusterKeywordsPreviewModel> {
        let request = await Network.put(ProductType.COMMUNITY, `/community/clusters/keywords/rename/${communityId}/${queryId}/${encodeURIComponent(newName)}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    static onClusterCreate(component: INetworkComponent, func: (clusterKeywordsPreviewModel: ICommunitySEOClusterKeywordsPreviewModel[]) => void) {
        this.events.push(func);
        component.onRemove(() => arrayRemove(this.events, func));
    }
}